/**
 * Hosting a brain that cannot answer instantly (a model behind a network call).
 *
 * The sim never waits. When a pilot has something to say it works out the rules answer first, asks
 * the other brain, and gives it a deadline in sim time. If the answer arrives in time and passes
 * the same checks, it is spoken; otherwise the rules answer is, and the miss is counted. That is
 * what keeps the flight loop deterministic in its timing whatever the model does.
 */
import type { PilotTrigger } from './types';

/** Seconds of sim time a brain gets, per trigger: [minimum, maximum]. */
export const BRAIN_DEADLINE_S: Record<PilotTrigger['kind'], readonly [number, number]> = {
  readback: [2, 6],
  unable: [2, 6],
  answer: [2, 8],
  conversation: [2, 8],
  reply: [2, 6],
  checkin: [3, 10],
  event: [3, 10],
  initiative: [3, 15],
  patience: [3, 15],
  fuel: [3, 10],
  emergency: [3, 10],
};

/** Wall-clock seconds a model is expected to take; time warp stretches it in sim seconds. */
export const DEFAULT_BUDGET_WALL_S = 4;
/** Answers still outstanding for one aircraft before the oldest is given up on. */
export const MAX_PENDING_PER_AIRCRAFT = 3;

export function deadlineFor(trigger: PilotTrigger, timeScale: number, budgetWallS = DEFAULT_BUDGET_WALL_S): number {
  const [min, max] = BRAIN_DEADLINE_S[trigger.kind];
  return Math.min(max, Math.max(min, budgetWallS * Math.max(1, timeScale)));
}

/** Why a decision was not used. */
export type BrainMiss = 'timeout' | 'invalid' | 'stale' | 'late';

export interface BrainLogEntry {
  /** Sim time the pilot was asked, and the time the answer was applied. */
  askedAtS: number;
  appliedAtS: number;
  callsign: string;
  trigger: PilotTrigger['kind'];
  source: 'rules' | 'llm' | 'replay' | 'fallback';
  miss?: BrainMiss;
  /** Why the sim refused the other brain's decision and spoke the rules brain's instead. */
  reason?: string;
  /** What the brain reported it cost, when it reports anything. */
  usage?: Record<string, number>;
}
